import { Component, Output, EventEmitter, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { AuthService } from '../../../services/auth.service';

@Component({
  selector: 'app-login-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './login-form.component.html',
  styleUrls: ['./login-form.component.scss']
})
export class LoginFormComponent {
  @Output() loginSuccess = new EventEmitter<void>();
  @Output() loginCancel = new EventEmitter<void>();

  loginForm: FormGroup;
  loading = signal(false);
  hidePassword = signal(true);
  errorMessage = signal<string | null>(null);

  // Forgot password state
  showForgotPassword = signal(false);
  forgotPasswordEmail = signal('');
  forgotPasswordSent = signal(false);
  forgotPasswordLoading = signal(false);
  forgotPasswordError = signal<string | null>(null);

  constructor(
    private fb: FormBuilder,
    private authService: AuthService
  ) {
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(6)]],
      rememberMe: [false]
    });
  }

  togglePasswordVisibility(): void {
    this.hidePassword.update(v => !v);
  }

  async onSubmit(): Promise<void> {
    if (this.loginForm.invalid) {
      Object.keys(this.loginForm.controls).forEach(key => {
        const control = this.loginForm.get(key);
        control?.markAsTouched();
      });
      return;
    }

    this.loading.set(true);
    this.errorMessage.set(null);

    try {
      const { email, password } = this.loginForm.value;
      await this.authService.signIn(email, password);
      this.loginSuccess.emit();
    } catch (error: any) {
      console.error('Login error:', error);
      this.errorMessage.set(this.getErrorMessage(error));
    } finally {
      this.loading.set(false);
    }
  }

  async signInWithGoogle(): Promise<void> {
    this.loading.set(true);
    this.errorMessage.set(null);

    try {
      await this.authService.signInWithGoogle();
      this.loginSuccess.emit();
    } catch (error: any) {
      console.error('Google sign-in error:', error);
      this.errorMessage.set(this.getErrorMessage(error));
    } finally {
      this.loading.set(false);
    }
  }

  onCancel(): void {
    this.loginCancel.emit();
  }

  private getErrorMessage(error: any): string {
    switch (error.code) {
      case 'auth/invalid-email':
        return 'Geçersiz e-posta adresi.';
      case 'auth/user-disabled':
        return 'Bu hesap devre dışı bırakılmış.';
      case 'auth/user-not-found':
        return 'Bu e-posta adresi ile kayıtlı kullanıcı bulunamadı.';
      case 'auth/wrong-password':
        return 'Hatalı şifre.';
      case 'auth/too-many-requests':
        return 'Çok fazla başarısız giriş denemesi. Lütfen daha sonra tekrar deneyin.';
      case 'auth/network-request-failed':
        return 'Ağ bağlantı hatası. İnternet bağlantınızı kontrol edin.';
      default:
        return 'Giriş yapılırken bir hata oluştu. Lütfen tekrar deneyin.';
    }
  }

  // Forgot password methods
  toggleForgotPassword(): void {
    this.showForgotPassword.update(v => !v);
    if (this.showForgotPassword()) {
      // Pre-fill email from login form
      const email = this.loginForm.get('email')?.value;
      if (email) {
        this.forgotPasswordEmail.set(email);
      }
    } else {
      // Reset forgot password state
      this.forgotPasswordSent.set(false);
      this.forgotPasswordError.set(null);
    }
  }

  async sendPasswordResetEmail(): Promise<void> {
    const email = this.forgotPasswordEmail();
    if (!email || !this.isValidEmail(email)) {
      this.forgotPasswordError.set('Lütfen geçerli bir e-posta adresi girin.');
      return;
    }

    this.forgotPasswordLoading.set(true);
    this.forgotPasswordError.set(null);

    try {
      await this.authService.sendPasswordResetEmail(email);
      this.forgotPasswordSent.set(true);
    } catch (error: any) {
      console.error('Password reset error:', error);
      this.forgotPasswordError.set(this.getPasswordResetErrorMessage(error));
    } finally {
      this.forgotPasswordLoading.set(false);
    }
  }

  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  private getPasswordResetErrorMessage(error: any): string {
    switch (error.code) {
      case 'auth/invalid-email':
        return 'Geçersiz e-posta adresi.';
      case 'auth/user-not-found':
        return 'Bu e-posta adresi ile kayıtlı kullanıcı bulunamadı.';
      case 'auth/too-many-requests':
        return 'Çok fazla deneme yapıldı. Lütfen daha sonra tekrar deneyin.';
      default:
        return 'Şifre sıfırlama e-postası gönderilirken bir hata oluştu.';
    }
  }

  backToLogin(): void {
    this.showForgotPassword.set(false);
    this.forgotPasswordSent.set(false);
    this.forgotPasswordError.set(null);
  }
}